import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { autoEstimateComponent } from '../../../services/autoEstimation';
import { supabase } from '../../../lib/supabase';

const BILLING_BYPASS_USER_IDS = new Set<string>([
  '7591c3af-8654-4f76-85b9-15d75da78043',
]);

const BILLING_BYPASS_UNTIL_MS = new Date('2026-03-23T23:59:59Z').getTime();

function isBillingBypassed(userId?: string) {
  return !!userId &&
    BILLING_BYPASS_USER_IDS.has(userId) &&
    Date.now() < BILLING_BYPASS_UNTIL_MS;
}


interface UseEstimationAndBillingParams {
  estimationId: string;
  userId?: string;
  setLaborEstimationProgress: Dispatch<
    SetStateAction<Array<{ name: string; count: number; status: 'pending' | 'processing' | 'complete' | 'error' }>>
  >;
  setShowLaborEstimationModal: Dispatch<SetStateAction<boolean>>;
}

export function useEstimationAndBilling({
  estimationId,
  userId,
  setLaborEstimationProgress,
  setShowLaborEstimationModal,
}: UseEstimationAndBillingParams) {
  const runConcurrentLaborEstimation = useCallback(
    async (components: Array<{ name: string; count: number }>) => {
      const progressItems = components.map((c) => ({
        name: c.name,
        count: c.count,
        status: 'pending' as const,
      }));

      setLaborEstimationProgress(progressItems);
      setShowLaborEstimationModal(true);

      const updateStatus = (name: string, status: 'pending' | 'processing' | 'complete' | 'error') => {
        setLaborEstimationProgress((prev) => prev.map((item) => (item.name === name ? { ...item, status } : item)));
      };

      const estimationPromises = components.map(async (component) => {
        try {
          updateStatus(component.name, 'processing');

          const result = await autoEstimateComponent(component.name, component.count, estimationId, (message) => {
            console.log(`[${component.name}] ${message}`);
          });

          if (result.success) {
            updateStatus(component.name, 'complete');
          } else {
            updateStatus(component.name, 'error');
          }

          return result;
        } catch (error) {
          console.error(`Error estimating ${component.name}:`, error);
          updateStatus(component.name, 'error');
          return { success: false, error: 'Estimation failed' };
        }
      });

      const results = await Promise.allSettled(estimationPromises);
      const successfulResults = results
        .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled' && r.value?.success)
        .map((r) => r.value);

      const necaCitations = successfulResults
        .filter((r) => r.laborData?.page_number)
        .map((r) => ({
          source: 'neca_manual',
          pageNumber: r.laborData.page_number,
          section: r.laborData.section || '',
          quote: `${r.laborData.matched_component}: ${r.laborData.measurement}`,
          componentName: r.componentName,
          laborHours: r.laborData.labor_hours,
        }));

      if (necaCitations.length > 0) {
        console.log(`✅ Collected ${necaCitations.length} NECA citations`);
      }
    },
    [estimationId, setLaborEstimationProgress, setShowLaborEstimationModal]
  );

  const reportUsageForFinishReview = useCallback(
    async (units: number) => {
      if (!estimationId || units <= 0) return;
      
      if (isBillingBypassed(userId)) {
        console.log('Billing bypass active: skipping usage report');
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken || !userId) {
        throw new Error('You must be signed in to record billing usage.');
      }

      const actionId = `finish-${estimationId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const response = await fetch('/api/billing/report-usage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          actionId,
          estimationId,
          units,
          source: 'finish_estimate_review',
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || payload?.details || `Billing usage request failed with status ${response.status}`);
      }

      console.log('✅ Billing usage recorded for finish action', { actionId, units });
    },
    [estimationId, userId]
  );

  return {
    runConcurrentLaborEstimation,
    reportUsageForFinishReview,
  };
}
